"use client";

import Link from "next/link";
import { FaChrome } from "@/components/icons";
import { cn } from "@/lib/utils";
import { getAllProducts, type ProductId } from "@/lib/products";
import { trackInstallButtonClicked } from "@/lib/analytics/events";

// Get full product entry from the source of truth — we need both the redirect
// URL and the comingSoon flag so coming-soon products render a disabled CTA
// linking to their marketing page instead of an install link that goes nowhere.
const getProductEntry = (productId: ProductId) => {
  return getAllProducts().find((p) => p.id === productId);
};

export type Product = ProductId;

interface InstallButtonProps {
  className?: string;
  children?: React.ReactNode;
  source?: string;
  source_id?: string;
  target?: "_blank" | "_self" | "_parent" | "_top";
  product?: Product; // Optional: defaults to x-unfollow-pro for backward compatibility
  onClick?: () => void;
}

export default function InstallButton({
  className,
  children,
  source,
  source_id,
  product = "x-unfollow-pro", // Default to x-unfollow-pro for backward compatibility
  onClick,
}: InstallButtonProps) {
  const handleClick = () => {
    // Track download event using the global xPixelTrack
    if (window.xPixelTrack?.trackDownload) {
      window.xPixelTrack.trackDownload({
        conversion_id: `install-${product}-${source || "unknown"}${source_id ? `-${source_id}` : ""}-${Date.now()}`,
      });
    }
    // PostHog/GTM: the on-site half of the install click (the X pixel above
    // only reaches X). Uses the slug so it joins cleanly with view_product
    // and /install/ pageviews in funnels.
    //
    // Falls back to "unknown", never to the raw product id. Every other event
    // in this funnel emits a slug, so an id here would split one product
    // across two values and quietly under-count it — the same failure this
    // change set exists to fix on the LemonSqueezy side, where legacy names
    // fell out of attribution entirely. A miss is currently unreachable (every
    // ProductId resolves), which is exactly why the fallback should say so
    // rather than inventing a plausible-looking value.
    trackInstallButtonClicked({
      product_slug: getProductEntry(product as ProductId)?.slug ?? "unknown",
      source,
      source_id,
    });
    // Call custom onClick handler if provided
    onClick?.();
  };

  // Resolve product entry from the registry (source of truth for both URL and
  // the comingSoon flag).
  const productEntry = getProductEntry(product as ProductId);

  // Coming-soon products: there's no Chrome Web Store listing yet, so a
  // standard install CTA would link nowhere useful. Render a disabled-looking
  // CTA that routes to the product page (which surfaces the coming-soon
  // marketing content + a "notify me" hook). Centralising the swap here means
  // every call site auto-handles the case without per-site edits.
  if (productEntry?.comingSoon) {
    return (
      <Link
        href={`/product/${productEntry.slug}/`}
        className={cn(
          "inline-flex items-center justify-center gap-2 bg-muted text-muted-foreground px-4 py-2 text-sm font-medium shadow-sm cursor-default opacity-80 hover:opacity-100",
          className
        )}
        aria-label={`${productEntry.alternateName} is coming soon — open product page`}
      >
        <FaChrome className="h-4 w-4" aria-hidden="true" />
        Coming Soon
      </Link>
    );
  }

  const redirectUrl = productEntry?.chromeStoreRedirect;

  // Safety check: if redirectUrl is undefined, log error and fallback to products page
  if (!redirectUrl) {
    console.error(
      `InstallButton: No redirect URL found for product "${product}". Falling back to products page.`
    );
    return (
      <Link
        href="/products/"
        rel="nofollow noopener noreferrer"
        className={cn(
          "inline-flex items-center justify-center gap-2 bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90",
          className
        )}
      >
        <div className="relative flex items-center justify-center">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-40"></span>
          <FaChrome className="relative h-4 w-4" />
        </div>
        {children || "Install Free Extension"}
      </Link>
    );
  }

  return (
    <Link
      href={redirectUrl}
      rel="nofollow noopener noreferrer"
      target="_blank"
      prefetch={false}
      className={cn(
        "inline-flex items-center justify-center gap-2 bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90",
        className
      )}
      onClick={handleClick}
    >
      <div className="relative flex items-center justify-center">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-40"></span>
        <FaChrome className="relative h-4 w-4" />
      </div>
      {children || "Install Free Extension"}
      <span className="sr-only">(opens in new tab)</span>
    </Link>
  );
}
